import { effect, inject, Injectable, Injector, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { EnvironmentService, WindowService } from '@big-direkt/utils/environment';
import { captureException } from '@sentry/browser';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { filter } from 'rxjs';
import { AuthenticationStrategy } from './authentication-strategy.interface';

@Injectable({
    providedIn: 'root',
})
export class OAuthAuthenticationStrategy implements AuthenticationStrategy {
    private readonly oauthService = inject(OAuthService);
    private readonly environment = inject(EnvironmentService);
    private readonly window = inject(WindowService);
    private readonly router = inject(Router);
    private readonly injector = inject(Injector);
    private readonly _isAuthenticated = signal<boolean>(false);
    private readonly authenticationEvent = toSignal(this.oauthService.events.pipe(filter(({ type }) => type === 'token_received')));
    private readonly errorEvent = toSignal(this.oauthService.events.pipe(filter(event => event instanceof OAuthErrorEvent)));

    public get isAuthenticated(): ReturnType<typeof signal<boolean>> {
        return this._isAuthenticated;
    }

    public async setup(): Promise<void> {
        if (!this.environment.authConfig) {
            return;
        }

        const { oauth } = this.environment.authConfig;

        if (!oauth) {
            return;
        }

        this.oauthService.configure({
            issuer: oauth.issuer,
            clientId: oauth.clientId,
            scope: oauth.scope,
            redirectUri: this.window.nativeWindow()?.location.origin,
            responseType: 'code',
            checkOrigin: true,
            strictDiscoveryDocumentValidation: false,
        });

        try {
            await this.oauthService.loadDiscoveryDocumentAndTryLogin();
            this.oauthService.setupAutomaticSilentRefresh();
        } catch (error) {
            this.handleError(error);
        }

        effect(() => {
            const event = this.authenticationEvent();
            if (event && this.oauthService.state) {
                void this.router.navigateByUrl(decodeURIComponent(this.oauthService.state));
            }
        }, { injector: this.injector });

        effect(() => {
            const event = this.errorEvent();
            if (event) {
                this.handleError(event);
            }
        }, { injector: this.injector });

        this._isAuthenticated.set(this.oauthService.hasValidAccessToken());
    }

    public login(destination?: string): undefined {
        if (!this.oauthService.hasValidAccessToken()) {
            this.oauthService.initLoginFlow(destination?.replace(/\/?logout/, ''));
        }
        this._isAuthenticated.set(this.oauthService.hasValidAccessToken());

        return undefined;
    }

    public async logout(): Promise<void> {
        this.oauthService.logOut();
        this._isAuthenticated.set(false);

        if (this.router.url.startsWith(this.environment.privatePath) && this.environment.user?.logoutRedirect) {
            await this.router.navigateByUrl(this.environment.user.logoutRedirect);
        }
    }

    public getToken(): string | undefined {
        return this.oauthService.getAccessToken();
    }

    private handleError(error: unknown): void {
        captureException(JSON.stringify(error));
        if (this.environment.user?.errorRedirect) {
            void this.router.navigateByUrl(this.environment.user.errorRedirect);
        }
    }
}
